<template>
  <div :class="{ 'ew-todo--dense': dense }" class="ew-todo">
    <div v-if="info.course || courseLink" class="ew-todo__course">
      <router-link v-if="courseLink" :to="courseLink" class="tag tag--sm tone-info ew-todo__link" title="Kurs öffnen" @click.native.stop>
        <v-icon class="tag__icon" x-small>mdi-music-clef-treble</v-icon><span class="text-truncate">{{ info.course || 'Kurs' }}</span>
      </router-link>
      <span v-else class="acq-tiny ew-todo__text">{{ info.course }}</span>
    </div>
    <div v-if="info.dots.length" class="ew-todo__dots">
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <span class="d-inline-flex align-center" style="gap: 6px" v-on="on">
            <presence-dots :items="info.dots"/>
            <span class="acq-tiny">älteste → neueste</span>
          </span>
        </template>
        <span>{{ legend }}</span>
      </v-tooltip>
    </div>
    <div v-if="!dense && (info.mail || studentLink)" class="ew-todo__tags">
      <router-link v-if="studentLink" :to="studentLink" class="tag tag--sm tone-info ew-todo__link" title="Profil der Schüler:in öffnen"
                   @click.native.stop>
        <v-icon class="tag__icon" x-small>mdi-school</v-icon><span class="text-truncate">{{ studentName || 'Schüler:in' }}</span>
      </router-link>
      <span v-if="info.mail" :class="`tone-${info.mail.tone}`" :title="info.mail.title" class="tag tag--sm ew-todo__tag">
        <v-icon class="tag__icon" x-small>{{ info.mail.icon }}</v-icon>{{ info.mail.text }}
      </span>
    </div>
    <div v-if="!dense && info.ruleName" class="acq-tiny">Regel „{{ info.ruleName }}“</div>
    <div v-if="!dense && info.extraText" class="acq-muted ew-todo__extra">{{ info.extraText }}</div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import {Types} from 'mongoose';
import {RawLocation} from 'vue-router/types/router';
import PresenceDots from '@/components/earlyWarning/presenceDots.vue';
import {DOTS_LEGEND, EarlyWarningToDoInfo} from '@/components/toDos/earlyWarningToDo.helper';
import {CrudEntityTypes} from '@/classes/clientOnly/permissionTreeResources/enums/CrudEntityTypes';

/**
 * Structured display of an early-warning to-do: course (link if readable), attendance dots (oldest → newest),
 * mail status, optional student link (only with read access) and the rule name.
 */
export default Vue.extend({
  components: {PresenceDots},
  props: {
    info: {type: Object as () => EarlyWarningToDoInfo, required: true},
    /** Student id: shows a link to the profile (only with read access). Leave empty where the student is shown anyway */
    studentId: {type: [String, Object] as unknown as () => string | Types.ObjectId | undefined, default: undefined},
    studentName: {type: String, default: ''},
    /** Only course and dots (compact lists such as the dashboard) */
    dense: {type: Boolean, default: false},
  },
  computed: {
    legend: () => DOTS_LEGEND,
    courseLink(): RawLocation | null {
      if (!this.info.courseId) {
        return null;
      }
      const entity = this.$$getCrudEntity(CrudEntityTypes.COURSE, this.info.courseId as unknown as Types.ObjectId);
      return entity.canRead() ? `/courses/${this.info.courseId}` : null;
    },
    studentLink(): RawLocation | null {
      if (!this.studentId) {
        return null;
      }
      const id = String(this.studentId);
      const entity = this.$$getCrudEntity(CrudEntityTypes.STUDENT, id as unknown as Types.ObjectId);
      return entity.canRead() ? `/students/${id}` : null;
    },
  },
});
</script>

<style scoped>
.ew-todo {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.ew-todo--dense {
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
}

.ew-todo__course, .ew-todo__tags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.ew-todo__link {
  max-width: 100%;
  text-decoration: none;
}

.ew-todo__text {
  overflow-wrap: anywhere;
}

.ew-todo__tag {
  height: auto;
  min-height: 20px;
  white-space: normal;
  overflow-wrap: anywhere;
}

.ew-todo__extra {
  font-size: 13px;
  white-space: pre-line;
  overflow-wrap: anywhere;
}
</style>
<style src="@/components/common/modern/modern.css"></style>
<style src="@/components/acquisitionList/ui/acquisitionViews.css"></style>
